import { useState } from "react";
import { Row, Col } from "react-bootstrap";
import Select from "react-select";
import { SALE_STATES } from "../../../core/blocks/AdPlan/constants";
import { formatCurrency } from "../../../../utils/formatNumber";
import { LAUNCH_STATES } from "./constants";
import { trellisPalette } from "../../analytics/palettes";
import { snakeCaseToSentenceCase } from "../../../../utils/formatText";
import generateLink from "../../../../utils/generateLink";
import ProductImage from "../../category/listings/ProductImage";
import { Link } from "@tanstack/react-router";
import api from "../../../../utils/api";
import { Button, buttonRecipe } from "../../../core/basic/Button";
import { useSimpleCan } from "../../../../config/casl/casl";
import { Trans, useTranslation } from "react-i18next";

const salesStateArray = [SALE_STATES.CLEARANCE, SALE_STATES.BEST_SELLER, SALE_STATES.NORMAL, SALE_STATES.NEW_PRODUCT];

// Maps SALE_STATES enum values to their corresponding merchandising-namespace saleState keys.
// Lets us render localized labels for the sale states displayed in this dialogue without
// refactoring SALE_STATES itself (it's shared across many out-of-batch consumers).
const SALE_STATE_KEY = {
  BEST_SELLER: "Best Seller",
  CLEARANCE: "Clearance",
  NORMAL: "Growth",
  NEW_PRODUCT: "New Product",
  INACTIVE: "Inactive",
};

const LaunchRetrospectiveDialogue = ({
  productDetails,
  launchData,
  onUpdateStatus,
  productId,
  productState: currentProductState,
  marketPlace,
}) => {
  const showNegativePerfOptions = false;
  const showPositivePerfOptions = false;
  const { t } = useTranslation(["merchandising"]);

  const userCanUpdateProducts = useSimpleCan("update", "Product");

  const [productState, setProductState] = useState(
    currentProductState === SALE_STATES.NEW_PRODUCT.value ?
      salesStateArray.find((s) => s.value === productDetails?.recommended_sales_state)
    : salesStateArray.find((s) => s.value === currentProductState),
  );
  const [editSalesState, setEditSalesState] = useState(false);

  let { id, asin, sku, stock_status, product_price, product_url, product_title, inventory_state } = productDetails;

  if (marketPlace.marketPlace === "USA") {
    product_url = product_url?.replace("usa", "com");
  }

  const onVineEnrollment = async (id) => {
    await api.patch(`/merchandise/gvproducts/${id}/`, {
      vine_program_enrolled: true,
    });
  };

  return (
    <Row
      style={{
        maxWidth: "100%",
        margin: "auto",
      }}
      className="pb-4"
    >
      <Col xs={12}>
        {launchData?.state?.toUpperCase() === LAUNCH_STATES.INTRODUCED.value ?
          <Row style={{ padding: "1rem 5rem" }}>
            <h3
              style={{
                fontWeight: 700,
                margin: "2rem 0",
                fontSize: "18px",
              }}
            >
              {t(($) => $.merchandising["Product Details"])}
            </h3>
            <Col xs={12} className="card mb-0" style={{ padding: "3rem 4rem 1rem" }}>
              <Row>
                <Col xs={2}>
                  <ProductImage id={id} />
                </Col>
                <Col xs={9}>
                  <h5
                    style={{
                      fontWeight: 700,
                      marginTop: "3rem",
                      paddingLeft: "1rem",
                      fontSize: "16px",
                      color: trellisPalette[13],
                    }}
                  >
                    {product_title}
                  </h5>
                </Col>
              </Row>
              <Row
                style={{
                  fontSize: "16px",
                  margin: "2rem 1.5rem",
                  maxWidth: "90%",
                  color: "#73706E",
                }}
              >
                <Col xs={6}>
                  <span style={{ fontWeight: 600 }}>{t(($) => $.merchandising["ASIN:"])}</span>
                </Col>
                <Col xs={6}>{asin}</Col>
              </Row>
              <Row
                style={{
                  fontSize: "16px",
                  margin: "2rem 1.5rem",
                  maxWidth: "90%",
                  color: "#73706E",
                }}
              >
                <Col xs={6}>
                  <span style={{ fontWeight: 600 }}>{t(($) => $.merchandising["SKU:"])}</span>
                </Col>
                <Col xs={6}>{sku}</Col>
              </Row>
              <Row
                style={{
                  fontSize: "16px",
                  margin: "2rem 1.5rem",
                  maxWidth: "90%",
                  color: "#73706E",
                }}
              >
                <Col xs={6}>
                  <span style={{ fontWeight: 600 }}>{t(($) => $.merchandising["Price:"])}</span>
                </Col>
                <Col xs={6}>
                  {formatCurrency(product_price, {
                    marketPlace: marketPlace.marketPlace,
                  })}
                </Col>
              </Row>
              {stock_status && (
                <Row
                  style={{
                    fontSize: "16px",
                    margin: "2rem 1.5rem",
                    maxWidth: "90%",
                    color: "#73706E",
                  }}
                >
                  <Col xs={6}>
                    <span style={{ fontWeight: 600 }}>{t(($) => $.merchandising["Stock Status:"])}</span>
                  </Col>
                  <Col xs={6}>{snakeCaseToSentenceCase(stock_status ?? inventory_state)}</Col>
                </Row>
              )}
              <Row
                style={{
                  fontSize: "16px",
                  margin: "2rem 1.5rem",
                  maxWidth: "90%",
                  color: "#73706E",
                }}
              >
                <Col xs={6}>
                  <span style={{ fontWeight: 600 }}>{t(($) => $.merchandising["Amazon Listing:"])}</span>
                </Col>
                <Col xs={6}>
                  <a
                    key={id}
                    href={product_url}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                    style={{
                      color: "#d91266",
                      textAlign: "center",
                      float: "center",
                      textDecoration: "none",
                      fontWeight: 600,
                      fontSize: "1.5rem",
                      cursor: "pointer",
                    }}
                  >
                    {t(($) => $.merchandising["Visit Listing"])}
                  </a>
                </Col>
              </Row>
              <Row
                className="mx-auto"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "1rem",
                }}
              >
                <Button variant="primary">
                  {generateLink(
                    "listing",
                    // What is below for?
                    {
                      ...productDetails,
                      product_category: parseInt(productDetails.category_name),
                    },
                    null,
                    "sku",
                    { color: "#FFF", fontWeight: 600, fontSize: "1.3rem" },
                    true,
                    t(($) => $.merchandising["View Details"]),
                  )}
                </Button>
              </Row>
            </Col>
          </Row>
        : <Row>
            <Col xs={12} md={6}>
              <Row style={{ padding: "1rem 5rem" }}>
                <h3
                  style={{
                    fontWeight: 700,
                    margin: "1rem 0",
                    fontSize: "18px",
                  }}
                >
                  {t(($) => $.merchandising["Product Status"])}
                </h3>
                <Col xs={12} className="card mb-0" style={{ padding: "3rem 4rem 1rem" }}>
                  {currentProductState === SALE_STATES.NEW_PRODUCT.value || editSalesState ?
                    <>
                      <h5
                        style={{
                          fontWeight: 600,
                          marginTop: 0,
                          fontSize: "16px",
                        }}
                      >
                        {t(($) => $.merchandising["Recommendations"])}
                      </h5>
                      <p
                        style={{
                          fontSize: "16px",
                          marginBottom: "2rem",
                          maxWidth: "90%",
                          color: "#73706E",
                        }}
                      >
                        {/* TODO: Below will be dynamic once recommended state sorted out */}
                        <Trans
                          t={t}
                          i18nKey={($) =>
                            $.merchandising[
                              "Based on performance throughout the launch, Trellis is recommending this product is labelled as a <strong>{label}</strong>."
                            ]
                          }
                          values={{
                            label:
                              SALE_STATE_KEY[productDetails?.recommended_sales_state] ?
                                t(
                                  ($) =>
                                    $.merchandising.saleState[SALE_STATE_KEY[productDetails.recommended_sales_state]],
                                )
                              : t(($) => $.merchandising["Not Found"]),
                          }}
                          components={{ strong: <strong /> }}
                        />
                      </p>
                      <Row>
                        <Col xs={6}>
                          <span
                            style={{
                              fontSize: "16px",
                              padding: "1rem 0",
                              display: "block",
                              fontWeight: 600,
                            }}
                          >
                            {t(($) => $.merchandising["Product Status"])}
                          </span>
                        </Col>
                        <Col xs={6} style={{ position: "relative" }}>
                          <Select
                            value={
                              productState ?
                                {
                                  label: t(($) => $.merchandising.saleState[SALE_STATE_KEY[productState.value]]),
                                  value: productState.value,
                                }
                              : null
                            }
                            options={[
                              SALE_STATES.NORMAL,
                              SALE_STATES.BEST_SELLER,
                              SALE_STATES.CLEARANCE,
                              SALE_STATES.NEW_PRODUCT,
                            ].map((state) => ({
                              label: t(($) => $.merchandising.saleState[SALE_STATE_KEY[state.value]]),
                              value: state.value,
                            }))}
                            onChange={(item) => {
                              setProductState(item);
                            }}
                            getOptionValue={(option) => option}
                          />
                        </Col>
                      </Row>
                      <Row
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          margin: "3rem 0 1rem",
                        }}
                      >
                        <Button
                          maxWidth="250px"
                          onPress={() => {
                            onUpdateStatus({
                              sales_state: productState.value,
                              id: productId,
                            });
                            setEditSalesState(false);
                          }}
                        >
                          {t(($) => $.merchandising["Accept Status"])}
                        </Button>
                      </Row>
                    </>
                  : <>
                      <h5
                        style={{
                          fontWeight: 600,
                          marginTop: 0,
                          fontSize: "16px",
                        }}
                      >
                        {t(($) => $.merchandising["Product Launch Outcome"])}
                      </h5>
                      <p
                        style={{
                          fontSize: "16px",
                          marginBottom: "2rem",
                          maxWidth: "90%",
                          color: "#73706E",
                        }}
                      >
                        <Trans
                          t={t}
                          i18nKey={($) =>
                            $.merchandising["This product has been classified as a <strong>{label}</strong> listing."]
                          }
                          values={{
                            label:
                              SALE_STATE_KEY[currentProductState] ?
                                t(($) => $.merchandising.saleState[SALE_STATE_KEY[currentProductState]])
                              : "",
                          }}
                          components={{ strong: <strong /> }}
                        />
                      </p>
                      {userCanUpdateProducts && (
                        <p
                          style={{
                            fontSize: "16px",
                            marginBottom: "2rem",
                            maxWidth: "90%",
                            color: "#73706E",
                          }}
                        >
                          <Trans
                            t={t}
                            i18nKey={($) => $.merchandising["<editLink>Click here</editLink> to update this status."]}
                            components={{
                              editLink: (
                                <span
                                  onClick={() => setEditSalesState(true)}
                                  style={{
                                    color: "#d91266",
                                    float: "center",
                                    fontWeight: "500",
                                    fontSize: "1.5rem",
                                    cursor: "pointer",
                                  }}
                                />
                              ),
                            }}
                          />
                        </p>
                      )}
                    </>
                  }
                </Col>
              </Row>
            </Col>
            {(([SALE_STATES.BEST_SELLER.value, SALE_STATES.NORMAL.value]?.includes(
              productDetails?.recommended_sales_state,
            ) &&
              launchData.state.toUpperCase() === LAUNCH_STATES.LAUNCHED.value) ||
              showPositivePerfOptions) && (
              <Col xs={12} md={6}>
                <Row style={{ padding: "1rem 5rem" }}>
                  <h3
                    style={{
                      fontWeight: 700,
                      margin: "1rem 0",
                      fontSize: "18px",
                    }}
                  >
                    {t(($) => $.merchandising["Increase Advertising Budget"])}
                  </h3>
                  <Col xs={12} className="card mb-0" style={{ padding: "3rem 4rem 1rem" }}>
                    <p
                      style={{
                        fontSize: "16px",
                        marginBottom: "2rem",
                        maxWidth: "90%",
                        color: "#73706E",
                      }}
                    >
                      {t(
                        ($) =>
                          $.merchandising[
                            "Based on the success of this product's launch, Trellis recommends increasing the advertising budget to maximize placement advertising opportunities."
                          ],
                      )}
                    </p>

                    <Row
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        margin: "3rem 0 1rem",
                      }}
                    >
                      {generateLink(
                        "advertisement",
                        {
                          ...productDetails,
                          category: parseInt(productDetails.category_name),
                        },
                        null,
                        "asin",
                        {},
                        true,
                        <Button>{t(($) => $.merchandising["View Media Plan"])}</Button>,
                      )}
                    </Row>
                  </Col>
                </Row>
              </Col>
            )}
          </Row>
        }
        <Row>
          {(([SALE_STATES.BEST_SELLER.value, SALE_STATES.NORMAL.value]?.includes(
            productDetails?.recommended_sales_state,
          ) &&
            launchData.state.toUpperCase() === LAUNCH_STATES.LAUNCHED.value) ||
            showPositivePerfOptions) && (
            <Col xs={12} md={6}>
              <Row style={{ padding: "1rem 5rem" }}>
                <h3
                  style={{
                    fontWeight: 700,
                    margin: "1rem 0",
                    fontSize: "18px",
                  }}
                >
                  {t(($) => $.merchandising["Vine Program"])}
                </h3>
                <Col xs={12} className="card mb-0" style={{ padding: "3rem 4rem 1rem" }}>
                  <p
                    style={{
                      fontSize: "16px",
                      marginBottom: "2rem",
                      maxWidth: "90%",
                      color: "#73706E",
                    }}
                  >
                    {t(
                      ($) =>
                        $.merchandising[
                          "Based on the success of this product's launch, Trellis recommends enrolling it in the Vine program to gain more reviews and boost product content."
                        ],
                    )}
                  </p>
                  <p
                    style={{
                      fontSize: "16px",
                      marginBottom: "2rem",
                      maxWidth: "90%",
                      color: "#73706E",
                    }}
                  >
                    <Trans
                      t={t}
                      i18nKey={($) =>
                        $.merchandising["<vineLink>Click here</vineLink> to learn more about the Vine program."]
                      }
                      components={{
                        vineLink: (
                          <a
                            href="https://www.amazon.ca/vine/about"
                            style={{
                              color: "#d91266",
                              textDecoration: "none",
                              fontWeight: "500",
                              fontSize: "1.5rem",
                              cursor: "pointer",
                            }}
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                      }}
                    />
                  </p>
                  {/* https://www.amazon.ca/vine/about */}
                  {userCanUpdateProducts && (
                    <Row
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        margin: "3rem 0 1rem",
                      }}
                    >
                      <Button
                        maxWidth="250px"
                        onPress={() => {
                          onVineEnrollment(productId);
                          setEditSalesState(false);
                        }}
                      >
                        {t(($) => $.merchandising["Mark Enrolled"])}
                      </Button>
                    </Row>
                  )}
                </Col>
              </Row>
            </Col>
          )}
        </Row>
        {/* Negative performance
              -> content optimizations
              -> pricing opportuntiies
              -> prime badge
          */}
        <Row>
          {(([SALE_STATES.CLEARANCE.value]?.includes(productDetails?.recommended_sales_state) &&
            launchData.state.toUpperCase() === LAUNCH_STATES.LAUNCHED.value) ||
            showNegativePerfOptions) && (
            <>
              <Col xs={12} md={6}>
                <Row style={{ padding: "1rem 5rem" }}>
                  <h3
                    style={{
                      fontWeight: 700,
                      margin: "1rem 0",
                      fontSize: "18px",
                    }}
                  >
                    {t(($) => $.merchandising["Optimize Content"])}
                  </h3>
                  <Col xs={12} className="card mb-0" style={{ padding: "3rem 4rem 1rem" }}>
                    <p
                      style={{
                        fontSize: "16px",
                        marginBottom: "2rem",
                        maxWidth: "90%",
                        color: "#73706E",
                      }}
                    >
                      {t(
                        ($) =>
                          $.merchandising[
                            "To improve product performance and increase organic traffic, Trellis recommends optimizing listing content."
                          ],
                      )}
                    </p>
                    <Row>
                      <Row
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          margin: "3rem 0 1rem",
                        }}
                      >
                        <Link to={`/user/content/seo`} className={buttonRecipe()}>
                          {t(($) => $.merchandising["View Product Content"])}
                        </Link>
                      </Row>
                    </Row>
                  </Col>
                </Row>
              </Col>
              <Col xs={12} md={6}>
                <Row style={{ padding: "1rem 5rem" }}>
                  <h3
                    style={{
                      fontWeight: 700,
                      margin: "2rem 0",
                      fontSize: "18px",
                    }}
                  >
                    {t(($) => $.merchandising["Optimize Price"])}
                  </h3>
                  <Col xs={12} className="card mb-0" style={{ padding: "3rem 4rem 1rem" }}>
                    <p
                      style={{
                        fontSize: "16px",
                        marginBottom: "2rem",
                        maxWidth: "90%",
                        color: "#73706E",
                      }}
                    >
                      {t(
                        ($) =>
                          $.merchandising[
                            "Optimizing this product listing's price is recommended to increase profit and/or demand."
                          ],
                      )}
                    </p>

                    <Row
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        margin: "3rem 0 1rem",
                      }}
                    >
                      <Link to="/user/pricing/dashboard" className={buttonRecipe()}>
                        {t(($) => $.merchandising["View Pricing Dashboard"])}
                      </Link>
                    </Row>
                  </Col>
                </Row>
              </Col>
              <Col xs={12} md={6}>
                <Row style={{ padding: "1rem 5rem" }}>
                  <h3
                    style={{
                      fontWeight: 700,
                      margin: "2rem 0",
                      fontSize: "18px",
                    }}
                  >
                    {t(($) => $.merchandising["Add Prime Badge"])}
                  </h3>
                  <Col xs={12} className="card mb-0" style={{ padding: "3rem 4rem 1rem" }}>
                    <p
                      style={{
                        fontSize: "16px",
                        marginBottom: "2rem",
                        maxWidth: "90%",
                        color: "#73706E",
                      }}
                    >
                      {t(
                        ($) =>
                          $.merchandising[
                            "Adding the Prime badge to this listing may help increase traffic. The Prime badge is automatically added to a listing when a product is fulfilled by Amazon."
                          ],
                      )}
                    </p>
                  </Col>
                </Row>
              </Col>
            </>
          )}
        </Row>
      </Col>
    </Row>
  );
};

export default LaunchRetrospectiveDialogue;
